import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Form, Input, InputNumber } from 'antd';
import { useGetManyTransports } from 'api/transports/use-get-many-transports';
import { ClientType } from 'types/client.ts';
import { ClientFormSelect } from 'components/common/client-form-select';
import { ContactFormSelect } from 'components/common/contact-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import { FormCurrencySelect } from 'components/common/form-currency-select/form-currency-select';
import { TransportFormSelect } from 'components/common/transport-form-select';
import { isTruckWithTrailer } from 'components/trips/trip-form/helpers';
import type { ITripFormProps } from 'components/trips/trip-form/types';
import { useGetTransportTypes } from 'components/trips/trip-form/use-get-transport-types';
import styles from './trip-form.module.scss';
import { trailerValidator, validationRule } from './validators';

export const TripForm: React.FC<ITripFormProps> = (props) => {
  const { form, formId, onFinish, initialValues, isTripStarted = false } = props;
  const { t } = useTranslation();

  const [carrierId, setCarrieId] = useState<number | undefined>(undefined);

  const truckId = Form.useWatch('truckId', form);
  const soldToPartners = Form.useWatch('soldToPartners', form);
  const trailerPlate = Form.useWatch('trailerPlate', form);
  const hasTrailerPlate = Boolean(trailerPlate);

  const handleCarrierChange = (option: { value: number; label: string }) => {
    form.setFieldValue('contact', undefined);
    form.setFieldValue('carrier', { value: option.value, label: option.label });
    setCarrieId(Number(option.value));
  };

  const handleContactChange = (option: { value: string; label: string }) => {
    form.setFieldValue('contact', { value: option.value, label: option.label });
  };

  const handleChangeTruckSubType = () => {
    form.setFieldsValue({ trailerId: undefined });
    form.validateFields(['trailerId']);
  };

  const { data: trucks } = useGetManyTransports({ limit: 100, filters: { type: 'truck' } });

  const { truckSubType, trailerSubTypes } = useGetTransportTypes({
    truckId,
    trucks: trucks?.data,
    onChange: handleChangeTruckSubType,
  });

  const isUpdatesDisabled = isTripStarted && !!initialValues;
  const trailerInputDisabled = isTruckWithTrailer(truckSubType);

  const { data: trailers } = useGetManyTransports({
    limit: 100,
    filters: { type: 'trailer', subTypes: trailerSubTypes },
  });

  useEffect(() => {
    if (initialValues?.carrier?.value) setCarrieId(initialValues.carrier.value);
  }, [initialValues]);

  useEffect(() => {
    form.validateFields(['trailerId']);
  }, [hasTrailerPlate]);

  return (
    <Form form={form} id={formId} layout='vertical' initialValues={initialValues} onFinish={onFinish}>
      <Form.Item name='costPrice' label={t('trip:cost_price')} rules={validationRule.costPrice}>
        <InputNumber
          placeholder='Cost price'
          type='number'
          className={styles.inputFullWidth}
          addonAfter={<FormCurrencySelect />}
          decimalSeparator=','
        />
      </Form.Item>

      <DataRangeFormPicker
        name='estimatedDate'
        label={t('trip:estimated_date')}
        className={styles.inputFullWidth}
        isRequired
      />

      <Form.Item
        name='soldToPartners'
        rules={validationRule.soldToPartners}
        valuePropName='checked'
        initialValue={false}>
        <Checkbox disabled={isUpdatesDisabled}>{t('trip:sold_to_partners')}</Checkbox>
      </Form.Item>

      {soldToPartners ? (
        <>
          <ClientFormSelect
            label='Carrier'
            name='carrier'
            placeholder='Select Carrier'
            rules={validationRule.carrier}
            handleClientChange={handleCarrierChange}
            clientTypes={[ClientType.carrier, ClientType.expedition]}
            disabled={isUpdatesDisabled}
          />
          <ContactFormSelect
            label='Contact'
            name='contact'
            placeholder='Select contact'
            rules={validationRule.contact}
            clientId={carrierId}
            handleContactChange={handleContactChange}
            disabled={isUpdatesDisabled}
          />
        </>
      ) : (
        <>
          <p>Info about vehicle</p>
          <TransportFormSelect
            name='truckId'
            placeholder='Select Truck'
            rules={validationRule.truckId}
            transports={trucks?.data}
            disabled={isUpdatesDisabled}
          />
          <TransportFormSelect
            name='trailerId'
            placeholder='Select Trailer'
            rules={[{ required: false, type: 'number' }, trailerValidator(truckSubType, hasTrailerPlate)]}
            transports={trailers?.data}
            disabled={trailerInputDisabled || isUpdatesDisabled}
          />

          <Form.Item
            name='trailerPlate'
            rules={validationRule.trailerPlate}
            extra='If the trailer is not from our company'>
            <Input placeholder={t('Trailer plate')} className={styles.inputFullWidth} disabled={trailerInputDisabled} />
          </Form.Item>
        </>
      )}
    </Form>
  );
};
